import React, {useEffect, useState} from 'react';
import Table from '../../../common/table/Table';
import {adminClientDeleteAPI, adminClientTableAPI} from "../../../../services/admin";
import {GetAdminClientTableResponseData} from "../../../../types/admin";
import {Column} from "../../../common/table/TableSection";
import {useNavigate} from "react-router-dom";
import styles from "../../../client/document/ClientDocumentTable.module.css";
import {ClientSort, ClientStatus, ClientTarget, ContractStatus} from "../../../../types/contract";
import {useAlert} from "../../../common/error/AlertContext";
import Button from "../../../common/button/Button";

//어드민 클라이언트 테이블
const AdminClientTable = () => {
    const {showAlert} = useAlert();
    const {showConfirm} = useAlert();
    const navigate = useNavigate();
    const [data, setData] = useState<GetAdminClientTableResponseData[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [pendingSearchTerm, setPendingSearchTerm] = useState('');
    const [searchType, setSearchType] = useState<ClientTarget>(ClientTarget.ALL);
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
    const [filters, setFilters] = useState<Record<string, any>>({
        contractStatus: ['계약', '만료', '미계약'],
        clientStatus: ['승인'],
        start: 'ALL',
        expire: 'ALL'
    });

    //정렬
    const [sort, setSort] = useState<{ field: string | null, direction: 'asc' | 'desc' | null }>({
        field: 'companyName',
        direction: 'asc'
    });

    // 필터 전체 초기화
    const resetAllFilters = () => {
        // 필터 초기화
        setFilters({
            contractStatus: ['계약', '만료', '미계약'],
            clientStatus: ['승인'],
            start: 'ALL',
            expire: 'ALL'
        });

        // 검색 관련 초기화
        setSearchTerm('');
        setPendingSearchTerm('');
        setSearchType(ClientTarget.ALL);

        // 페이지 초기화
        setPage(1);

        // 정렬 초기화
        setSort({
            field: 'companyName',
            direction: 'asc'
        });

        // 선택된 행 초기화
        setSelectedRows(new Set());
    };

    useEffect(() => {
        const checkboxes = document.querySelectorAll('input[type="checkbox"]');
        const headerCheckbox = checkboxes[0] as HTMLInputElement;
        if (headerCheckbox) {
            headerCheckbox.indeterminate = selectedRows.size > 0 && selectedRows.size < data.length;
        }
    }, [selectedRows, data]);

    //체크박스 전체 선택
    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const allIds = data.map(row => row.clientId);
            setSelectedRows(new Set(allIds));
        } else {
            setSelectedRows(new Set());
        }
    };

    //클라이언트 삭제
    const handleDelete = async () => {
        if (selectedRows.size === 0) {
            showAlert('삭제할 항목을 선택해주세요.', "error");
            return;
        }
        //확인창
        showConfirm(
            '선택한 항목을 삭제하시겠습니까?',
            async () => {
                try {
                    //클라이언트 삭제 API, 체크박스 체크된 클라이언트 대상
                    await adminClientDeleteAPI(selectedRows);
                    showAlert('선택한 항목이 삭제되었습니다.', "info");
                    await fetchData();
                    setSelectedRows(new Set());
                } catch (error) {
                    console.error('Delete failed:', error);
                }
            }
        )
    };

    //체크박스 단일 선택
    const handleSelectRow = (clientId: number, checked: boolean) => {
        const newSelected = new Set(selectedRows);
        if (checked) {
            newSelected.add(clientId);
        } else {
            newSelected.delete(clientId);
        }
        setSelectedRows(newSelected);
    };

    //테이블 로우 선택 시
    const handleRowClick = (row: GetAdminClientTableResponseData) => {
        //세부 페이지로 이동
        navigate(`/admin/clients/account/${row.clientId}`);
    };

    //컬럼
    const columns: Column<GetAdminClientTableResponseData>[] = [
        //체크박스
        {
            key: 'checkbox',
            header: (
                <input
                    type="checkbox"
                    checked={data.length > 0 && selectedRows.size === data.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={styles.checkbox}
                />
            ),
            width: '40px',
            render: (_, row) => (
                <input
                    type="checkbox"
                    checked={selectedRows.has(row.clientId)}
                    onChange={(e) => handleSelectRow(row.clientId, e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    className={styles.checkbox}
                />
            )
        },
        {
            key: 'companyName',
            header: '회사명',
            width: '40%',
            sortable: true
        },
        {
            key: 'businessNumber',
            header: '사업자등록번호',
            width: '40%'
        },
        {
            key: 'clientStatus',
            header: '승인 여부',
            width: '20%'
        }
    ];

    //계정 상태 필터
    const approveFilterOptions = [
        {label: '승인', value: '승인'},
        {label: '대기', value: '대기'},
        {label: '반려', value: '반려'},
        {label: '만료', value: '만료'}
    ];

    //테이블 필터 설정
    const tableFilters = [
        {
            key: 'clientStatus',
            label: '승인 여부',
            options: approveFilterOptions,
            multiple: true,
            value: filters.clientStatus,
            component: undefined
        }
    ];

    //정렬 기준 설정
    const getSortParam = (field: string | null, direction: 'asc' | 'desc' | null): ClientSort => {
        if (!field || !direction) return ClientSort.COMPANY_ASC;
        const sortMap: Record<string, Record<string, ClientSort>> = {
            companyName: {
                asc: ClientSort.COMPANY_ASC,
                desc: ClientSort.COMPANY_DESC
            }
        };
        return sortMap[field]?.[direction] || ClientSort.COMPANY_ASC;
    };

    //검색
    const handleSearch = () => {
        setSearchTerm(pendingSearchTerm);
        setPage(1);
    };

    //엔터키 사용 검색
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    //테이블 데이터 설정
    const fetchData = async () => {
        try {
            setLoading(true);
            //클라이언트 데이터 조회 API 호출
            const response = await adminClientTableAPI({
                page: page - 1,
                sort: getSortParam(sort.field, sort.direction),
                search: searchTerm,
                target: searchType,
                clientStatus: filters.clientStatus as ClientStatus[],
                contractStatus: filters.contractStatus as ContractStatus[],
                start: filters.start,
                expire: filters.expire
            });
            //데이터 설정
            setData(response.content);
            //페이지 설정
            setTotalPages(Math.ceil(response.total / 7));
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, searchTerm, searchType, sort, filters]);

    //검색 대상 설정
    const searchOptions = [
        {value: ClientTarget.ALL, label: '전체'},
        {value: ClientTarget.COMPANY_NAME, label: '회사명'},
        {value: ClientTarget.BUSINESS_NUMBER, label: '사업자등록번호'}
    ];

    //필터값 변경
    const handleFilterChange = (key: string, value: any) => {
        setFilters(prev => {
            //클라이언트 상태 필터 변경
            if (key === 'clientStatus') {
                const newStatuses = value;
                if (newStatuses.length === 0) {
                    return {...prev, [key]: ['승인']};
                }
                return {...prev, clientStatus: newStatuses};
            }
            return {...prev, [key]: value};
        });
        setPage(1);
    };

    //정렬 변경
    const handleSortChange = (field: string | null, direction: 'asc' | 'desc' | null) => {
        setSort({field, direction});
    };

    return (
        <div className={styles.container}>
            <div className={styles.searchContainer}>
                <div className={styles.titleContainer}>
                    <div className={styles.padding}/>
                    <h3>계정</h3>
                    <div className={styles.mobileDeleteButtons}>
                        {/*삭제 버튼*/}
                        <Button
                            variant="secondary"
                            onClick={handleDelete}
                            disabled={selectedRows.size === 0}
                            size={"small"}
                        >
                            삭제
                        </Button>
                    </div>
                </div>
                <div className={styles.searchInputContainer}>
                    <div>
                        {/*검색 대상 선택*/}
                        <select
                            value={searchType}
                            onChange={(e) => setSearchType(e.target.value as ClientTarget)}
                            className={styles.searchTypeSelect}
                        >
                            {searchOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.searchInputWrapper}>
                        {/*검색 창*/}
                        <input
                            type="text"
                            placeholder="검색어를 입력하세요"
                            value={pendingSearchTerm}
                            onChange={(e) => setPendingSearchTerm(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className={styles.searchInput}
                        />
                        {/*검색 버튼*/}
                        <Button
                            onClick={handleSearch}
                            className={styles.searchButton}
                            variant="primary"
                            size={"small"}
                        >
                            검색
                        </Button>
                    </div>
                    {/*삭제 버튼*/}
                    <Button
                        onClick={handleDelete}
                        variant='secondary'
                        disabled={selectedRows.size === 0}
                        size={"small"}
                        className={styles.deleteButton}
                    >
                        삭제
                    </Button>
                </div>
            </div>
            {/*테이블*/}
            <Table
                columns={columns}
                data={data}
                loading={loading}
                size="medium"
                filters={tableFilters}
                showPagination={true}
                page={page}
                pageSize={7}
                totalItems={totalPages * 7}
                onPageChange={setPage}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
                onRowClick={handleRowClick}
                onReset={resetAllFilters}
            />
        </div>
    );
};

export default AdminClientTable;